import React, { useState } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import {
    Rating,
    TextField,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Divider,
    Paper,
    Box,
    Typography,
    Button,
    Alert,
    AlertTitle,
    CircularProgress,
} from '@mui/material';
import config from '../utils/Constants';

const FEEDBACK_TAGS = [
    { key: 'icd10_error', label: 'ICD-10 error' },
    { key: 'formatting_error', label: 'Formatting error' },
    { key: 'missing_field', label: 'Missing field' },
    { key: 'feature_request', label: 'Feature request' },
    { key: 'other', label: 'Other' },
];

/**
 * Multi-dimension feedback widget for admission records.
 *
 * Props:
 *   admissionId  {string}  - The admission record ID
 *   storageKey   {string}  - localStorage key used to gate re-render (optional, defaults to feedbackSubmitted:{admissionId})
 */
function FeedbackWidget({ admissionId, storageKey }) {
    const apiUrl = config.getApiUrl();
    const feedbackKey = storageKey || (admissionId ? `feedbackSubmitted:${admissionId}` : null);

    const [submitted, setSubmitted] = useState(() => feedbackKey ? !!localStorage.getItem(feedbackKey) : false);
    const [ratings, setRatings] = useState({
        usability_rating: null,
        output_quality_rating: null,
        integration_rating: null,
    });
    const [tags, setTags] = useState({
        icd10_error: false,
        formatting_error: false,
        missing_field: false,
        feature_request: false,
        other: false,
    });
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async () => {
        if (!admissionId) return;
        setSubmitting(true);
        setError(null);
        let token;
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.accessToken) throw new Error('Not authenticated');
            token = session.tokens.accessToken.toString();
        } catch (e) {
            console.error('Feedback submit error:', e);
            setError('Submission failed — please try again.');
            setSubmitting(false);
            return;
        }
        try {
            const selectedTags = Object.entries(tags)
                .filter(([, v]) => v)
                .map(([k]) => k);

            const resp = await fetch(
                `${apiUrl}/api/catalog/admissions/${admissionId}/feedback`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        usability_rating: ratings.usability_rating,
                        output_quality_rating: ratings.output_quality_rating,
                        integration_rating: ratings.integration_rating,
                        error_tags: selectedTags,
                        comments: comment || null,
                    }),
                }
            );

            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Error ${resp.status}`);
            }

            if (feedbackKey) { localStorage.setItem(feedbackKey, '1'); }
            setSubmitted(true);
        } catch (e) {
            setError(e.message || 'Submission failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <Alert severity="success" style={{ marginTop: '32px' }}>
                <AlertTitle>Thanks — feedback received</AlertTitle>
                We appreciate you taking the time to rate this record.
            </Alert>
        );
    }

    return (
        <Paper elevation={2} style={{ padding: '24px', marginTop: '32px' }}>
            <Typography variant="h6" gutterBottom>Rate This Admission Record</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Your feedback helps us improve Cynthia's output quality.
            </Typography>
            <Divider style={{ marginBottom: '16px' }} />

            {/* 3-axis ratings */}
            <Box mb={2}>
                <Typography component="legend">Usability</Typography>
                <Rating
                    name="usability_rating"
                    value={ratings.usability_rating}
                    onChange={(e, val) => setRatings(r => ({ ...r, usability_rating: val }))}
                />
            </Box>
            <Box mb={2}>
                <Typography component="legend">Output Quality</Typography>
                <Rating
                    name="output_quality_rating"
                    value={ratings.output_quality_rating}
                    onChange={(e, val) => setRatings(r => ({ ...r, output_quality_rating: val }))}
                />
            </Box>
            <Box mb={2}>
                <Typography component="legend">Pipeline Integration</Typography>
                <Rating
                    name="integration_rating"
                    value={ratings.integration_rating}
                    onChange={(e, val) => setRatings(r => ({ ...r, integration_rating: val }))}
                />
            </Box>

            {/* Error tag checkboxes */}
            <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>Flag an issue (optional)</Typography>
                <FormGroup row>
                    {FEEDBACK_TAGS.map(({ key, label }) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    checked={tags[key]}
                                    onChange={(e) => setTags(t => ({ ...t, [key]: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label={label}
                        />
                    ))}
                </FormGroup>
            </Box>

            {/* Comment box */}
            <Box mb={2}>
                <TextField
                    label="Additional comments (optional)"
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </Box>

            {error && (
                <Alert severity="error" style={{ marginBottom: '12px' }}>{error}</Alert>
            )}

            <Button
                variant="contained"
                color="primary"
                disabled={!admissionId || submitting || submitted}
                onClick={handleSubmit}
            >
                {submitting ? (
                    <><CircularProgress size={16} style={{ marginRight: 8 }} />Submitting...</>
                ) : (
                    'Submit Feedback'
                )}
            </Button>
        </Paper>
    );
}

export default FeedbackWidget;
